import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Check as CheckIcon, Close as CloseIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useChangePassword, usePasswordPolicy } from '@/containers/Login/hooks/useAuth';
import { useAppDispatch } from '@/store/redux';
import { updateUser } from '@/store/redux/slices/authSlice';
import { ROUTES } from '@/routes/config';

const ChangePassword: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const changePassword = useChangePassword();
  const { data: policy } = usePasswordPolicy();

  const rules = useMemo(() => {
    const p = policy ?? {
      min_length: 12,
      require_uppercase: true,
      require_lowercase: true,
      require_number: true,
      require_special: true,
    };
    const r: { label: string; test: (pw: string) => boolean }[] = [
      { label: `At least ${p.min_length} characters`, test: (pw) => pw.length >= p.min_length },
    ];
    if (p.require_uppercase) r.push({ label: 'One uppercase letter', test: (pw) => /[A-Z]/.test(pw) });
    if (p.require_lowercase) r.push({ label: 'One lowercase letter', test: (pw) => /[a-z]/.test(pw) });
    if (p.require_number) r.push({ label: 'One number', test: (pw) => /[0-9]/.test(pw) });
    if (p.require_special) r.push({ label: 'One special character (!@#$%^&*)', test: (pw) => /[!@#$%^&*]/.test(pw) });
    return r;
  }, [policy]);

  const passwordErrors = useMemo(
    () => rules.filter((rule) => !rule.test(newPassword)),
    [newPassword, rules]
  );

  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const isPasswordValid = newPassword.length > 0 && passwordErrors.length === 0;
  const isFormValid = currentPassword.length > 0 && isPasswordValid && passwordsMatch;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await changePassword
      .mutateAsync({ current_password: currentPassword, new_password: newPassword })
      .then(() => {
        dispatch(updateUser({ mustChangePassword: false }));
        navigate(ROUTES.DASHBOARD_HOME);
      })
      .catch(() => {
        // Error handled by mutation.error in the UI
      });
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Button
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIcon fontSize="small" />}
            size="small"
            sx={{ alignSelf: 'flex-start', mb: 1, color: 'text.secondary' }}
          >
            Back
          </Button>
          <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
            Change Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            For security, you must update your password before continuing.
          </Typography>

          {changePassword.error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {changePassword.error.message || 'Password change failed. Please try again.'}
            </Alert>
          )}

          {changePassword.isSuccess && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              Password updated successfully. Redirecting...
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Current Password"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={changePassword.isPending}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={changePassword.isPending}
            />

            {/* Password policy checklist */}
            {newPassword.length > 0 && (
              <Stack spacing={0.5} sx={{ mt: 1, mb: 1 }}>
                {rules.map((rule) => {
                  const passed = rule.test(newPassword);
                  return (
                    <Box key={rule.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {passed ? (
                        <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      ) : (
                        <CloseIcon sx={{ fontSize: 16, color: 'error.main' }} />
                      )}
                      <Typography
                        variant="caption"
                        color={passed ? 'success.main' : 'error.main'}
                      >
                        {rule.label}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              label="Confirm New Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={confirmPassword.length > 0 && !passwordsMatch}
              helperText={
                confirmPassword.length > 0 && !passwordsMatch ? 'Passwords do not match' : ' '
              }
              disabled={changePassword.isPending}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 1 }}
              disabled={!isFormValid || changePassword.isPending}
              startIcon={changePassword.isPending ? <CircularProgress size={20} /> : null}
            >
              {changePassword.isPending ? 'Updating...' : 'Update Password'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ChangePassword;
